// ── O MASCOTE DO APARELHO ─────────────────────────────────────────────────────
// Toda conta nasce com um dos três mascotes (Marte Bravo, Terra Brava, Lua Soldado), sorteado — e quem
// sorteia é ESTE aparelho, não o servidor.
//
// ⚠️ O MOTIVO É A ORDEM DA ESTREIA: o aparelho novo entra no tutorial ANTES de a conta existir
// (`estreiaRapida` em state/actions.js; a conta nasce por trás e só a primeira sala a espera). O tutorial
// precisa de um planeta já no primeiro frame, e a primeira sala tem que ter o MESMO — um planeta trocando
// na entrada da sala foi lido como defeito nos playtests da Poki. Então o aparelho sorteia uma vez, guarda,
// usa no tutorial e manda como DICA no pedido que cria a conta (`mascote`, validado por `nasceCom` em
// server/src/api/auth.js). Sem a dica, o servidor sorteia sozinho.
// ⚠️ MEMOIZADO NA CARGA DA PÁGINA, e não é zelo: `localStorage` LANÇA em janela anônima e em origem opaca, e
// sem a memória cada chamada sortearia de novo — o tutorial sairia com um mascote e a conta com outro.
// Sem storage, a degradação é sortear de novo no próximo F5 (antes de haver conta), nunca dentro da sessão.
// @ts-check
import { MASCOT_SKINS, SKINS_LIVRES, SKIN_BY_ID, SKIN_TUTORIAL, PROGRESSO, sorteiaMascote } from "@warspace/shared";

const KEY = "warspace_mascote";
let memo = -1;

/** O mascote DESTE aparelho: o guardado, ou um sorteado agora (e guardado). */
export function mascoteLocal() {
  if (memo >= 0) return memo;
  let v = NaN;
  try { v = Number(localStorage.getItem(KEY)); } catch { /* anônima/opaca/node: sem storage */ }
  if (!MASCOT_SKINS.includes(v)) { v = sorteiaMascote(Math.random); try { localStorage.setItem(KEY, String(v)); } catch { /* idem */ } }
  return (memo = v);
}

/**
 * COM QUE MASCOTE O TUTORIAL É JOGADO — o mesmo com que a primeira sala vai abrir.
 *   1. a conta já está com um mascote equipado → ele;
 *   2. a conta tem um mascote na coleção (e equipou outra coisa) → o dela, não um sorteado;
 *   3. conta ANTIGA, sem mascote, nas `PROGRESSO.PARTIDAS` primeiras partidas → o Marte. É o LEGADO da skin
 *      em teste da 1.1: o servidor dá a ela o Marte na próxima entrada (`garanteMascote`), então o tutorial
 *      com outro mascote faria o planeta trocar na primeira sala;
 *   4. sem conta ainda (a estreia) ou conta local → o do aparelho, que é o que vai como dica na criação.
 * @param {{user?:any,skins?:number[],stats?:any}|null|undefined} sess `app.get().session`
 */
export function mascoteDaEstreia(sess) {
  const u = sess && sess.user;
  const eq = u ? u.equippedSkin | 0 : -1;
  if (MASCOT_SKINS.includes(eq)) return eq;
  const tem = ((sess && sess.skins) || []).find(id => MASCOT_SKINS.includes(id | 0));
  if (tem != null) return tem | 0;
  if (u && u.id !== "local" && (((sess && sess.stats) || {}).games | 0) < PROGRESSO.PARTIDAS) return SKIN_TUTORIAL;
  return mascoteLocal();
}

/**
 * A GRADE DE SKINS DA PAUSA, nesta ordem e sem repetição:
 *   1. o(s) MASCOTE(s) da conta — a "skin ganha", que fica à mão para VOLTAR a ela;
 *   2. as 10 LIVRES (`SKINS_LIVRES`: a Padrão + as 9 comuns), que toda conta pode usar;
 *   3. o resto do que a conta tem (comprou, conquistou, ganhou por anúncio) — a grade antiga listava só
 *      isso, e quem já tinha uma skin cara não pode perdê-la de vista por causa das dez.
 * ⚠️ `SKIN_BY_ID.get` e NUNCA `skinById` como filtro: aquele cai no `SKINS[0]` para id desconhecido (um
 * pod com catálogo mais novo manda um id que esta build não tem), e a grade ganharia um Planeta Padrão
 * repetido sem nada explicando.
 * ⚠️ Mora aqui, num `.js`, e não no `ui/Pause.jsx` que a desenha: o `node --test` não carrega `.jsx`.
 * ⚠️ As livres já vêm DENTRO de `session.skins` (o servidor as une à lista de posse), então a ordem é
 * a única coisa que esta função decide — e a grade não depende de a resposta ter chegado para mostrá-las.
 * @param {number[]|undefined} tem a lista de posse (`session.skins`)
 */
export function gradeDeSkins(tem) {
  const minhas = new Set((tem || []).map(x => x | 0));
  const ids = [...MASCOT_SKINS.filter(id => minhas.has(id)), ...SKINS_LIVRES,
    ...[...minhas].filter(id => !MASCOT_SKINS.includes(id) && !SKINS_LIVRES.has(id)).sort((a, b) => a - b)];
  return [...new Set(ids)].map(id => SKIN_BY_ID.get(id)).filter(Boolean);
}
